import { forwardRef, type ComponentPropsWithoutRef, type HTMLAttributes, type ReactNode } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useTranslation } from 'react-i18next';

/**
 * DS-43 — the modal window of the system. Overlay slate-900/60, white panel in a
 * slate-400 frame, dark slate-800 header, light zoom-in. Nothing here is
 * decorative: the header carries the record you are editing, the body is the only
 * thing that scrolls, and the footer keeps the actions on screen at all times.
 */

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const SIZE_CLASS: Record<DialogSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-6xl',
};

type ContentProps = ComponentPropsWithoutRef<typeof RadixDialog.Content>;

export interface DialogProps extends Omit<ContentProps, 'title' | 'children'> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Arabic. Shown in the dark header and announced as the dialog name. */
  title: ReactNode;
  /**
   * Second line under the title — the record, the invoice number, the patient.
   * White, not muted: it carries facts the form no longer asks for (the booked
   * slot, the file number), so it has to read as content and not as a caption.
   */
  subtitle?: ReactNode;
  size?: DialogSize;
  children?: ReactNode;
  /** Action buttons. Wrapped in a `DialogFooter` for you. */
  footer?: ReactNode;
  closeOnOverlayClick?: boolean;
  /**
   * Blocks the two *accidental* ways out — Escape and the overlay — while a form
   * is dirty or a save is in flight. The header X still calls `onOpenChange`, so
   * the deliberate close keeps running through the caller's own guard.
   */
  preventClose?: boolean;
  /**
   * Removes the header X too, for a dialog that must not be left at all until
   * it says so (e.g. waiting on the government's answer). Pair with
   * `preventClose`.
   */
  hideCloseButton?: boolean;
  closeLabel?: string;
  /** Applied to the scrolling body, e.g. `p-0` for a full-bleed table. */
  bodyClassName?: string;
}

export const Dialog = forwardRef<HTMLDivElement, DialogProps>(function Dialog(
  {
    open,
    onOpenChange,
    title,
    subtitle,
    size = 'md',
    children,
    footer,
    closeOnOverlayClick = true,
    preventClose = false,
    hideCloseButton = false,
    closeLabel: closeLabelProp,
    className,
    bodyClassName,
    onEscapeKeyDown,
    onInteractOutside,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation('common');
  const closeLabel = closeLabelProp ?? t('actions.close');
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-slate-900/60" />

        {/* DS-50 — the panel is centred by a full-screen flex layer rather than a
            translate, so nothing has to know which way the document runs. The
            layer is click-through, so an outside click still lands on the overlay
            and Radix still reads it as an outside interaction.

            NFR-05 — the gutter goes below 1024px so the panel fills the screen.
            A centred card inside a phone viewport is the worst of both: the form
            is narrower than it needs to be and the strip of overlay around it is
            just close-by-accident waiting to happen on a dirty form. */}
        <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4 max-lg:p-0">
          <RadixDialog.Content
            ref={ref}
            className={cn(
              'pointer-events-auto flex max-h-[85vh] w-full flex-col overflow-hidden rounded',
              'animate-zoomIn border border-slate-400 bg-white shadow-2xl',
              // Full-bleed sheet on a phone: no frame, no corners, and the body
              // gets every pixel between the header and the footer.
              'max-lg:h-full max-lg:max-h-none max-lg:max-w-none max-lg:rounded-none max-lg:border-0',
              SIZE_CLASS[size],
              className,
            )}
            onEscapeKeyDown={(event) => {
              if (preventClose) event.preventDefault();
              onEscapeKeyDown?.(event);
            }}
            onInteractOutside={(event) => {
              if (preventClose || !closeOnOverlayClick) event.preventDefault();
              onInteractOutside?.(event);
            }}
            // Radix warns when a dialog has no description; without a subtitle
            // there genuinely is none, and an explicit undefined is the opt-out.
            {...(subtitle ? {} : { 'aria-describedby': undefined })}
            data-testid="dialog"
            {...rest}
          >
            <header
              className="flex shrink-0 items-start gap-3 bg-slate-800 px-4 py-2.5 text-white
                         max-lg:items-center max-lg:px-2 max-lg:pb-2
                         max-lg:pt-[calc(0.625rem+env(safe-area-inset-top,0px))]"
            >
              <div className="min-w-0 flex-1 max-lg:ps-1">
                <RadixDialog.Title data-testid="dialog-title" className="truncate text-sm font-bold leading-tight">
                  {title}
                </RadixDialog.Title>
                {subtitle ? (
                  <RadixDialog.Description className="mt-0.5 truncate text-[11px] text-white">
                    {subtitle}
                  </RadixDialog.Description>
                ) : null}
              </div>

              {hideCloseButton ? null : (
                <RadixDialog.Close
                  aria-label={closeLabel}
                  data-testid="dialog-close"
                  className="-me-1 shrink-0 rounded p-1 text-slate-300 transition hover:bg-slate-700 hover:text-white
                             max-lg:-me-0 max-lg:flex max-lg:h-touch max-lg:w-touch max-lg:items-center max-lg:justify-center"
                >
                  <X className="h-4 w-4 max-lg:h-5 max-lg:w-5" aria-hidden />
                </RadixDialog.Close>
              )}
            </header>

            {/* DS-01 — the body scrolls, the page never does. */}
            <div
              className={cn(
                'touch-scroll flex-1 overflow-y-auto overscroll-contain p-4 max-lg:p-3',
                bodyClassName,
              )}
            >
              {children}
            </div>

            {footer ? <DialogFooter>{footer}</DialogFooter> : null}
          </RadixDialog.Content>
        </div>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
});

/**
 * The action row. Passed to `Dialog`'s `footer` in the normal case; exported for
 * the form case, where the submit button has to sit inside the `<form>` in
 * `children` — render `<DialogFooter>` at the end of the form, or keep it in
 * `footer` and point the button at the form with `form="<form id>"`.
 */
export const DialogFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function DialogFooter({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          'flex shrink-0 items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-2.5',
          // NFR-05 — on a phone the actions share the width of the sheet rather
          // than huddling on the end edge, which is the corner a thumb reaches
          // last. Only direct buttons stretch: a footer that also carries a
          // running total or a hint keeps that text its natural size.
          'max-lg:px-3 max-lg:pt-2 max-lg:pb-[calc(0.625rem+env(safe-area-inset-bottom,0px))]',
          'max-lg:[&>button]:flex-1',
          className,
        )}
        data-testid="dialog-footer"
        {...rest}
      />
    );
  },
);
